import { AppInsights } from 'applicationinsights-js';
import { EventTelemetry } from '../telemetry-types/event-telemetry';
import { ExceptionTelemetry } from '../telemetry-types/exception-telemetry';
import { HttpRequestTelemetry } from '../telemetry-types/http-request-telemetry';
import { PageViewTelemetry } from '../telemetry-types/page-view-telemetry';
import { TraceTelemetry } from '../telemetry-types/trace-telemetry';

const instrumentationKey = typeof process.env.REACT_APP_INSIGHTS_KEY === 'string' ? process.env.REACT_APP_INSIGHTS_KEY : '';

const DEFAULT_CONFIG: Readonly<Microsoft.ApplicationInsights.IConfig> = {
  instrumentationKey,
  verboseLogging: process.env.REACT_APP_ENV !== 'production'
};

type IAppInsightsMethods = keyof Pick<
  Microsoft.ApplicationInsights.IAppInsights,
  'trackDependency' | 'trackEvent' | 'trackException' | 'trackMetric' | 'trackPageView' | 'trackTrace'
>;

// ==
// copied as-is from application insights dist bundle
function newId() {
  const base64chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  let result = '';
  let random = Math.random() * 1073741824; // 5 symbols in base64, almost maxint
  while (random > 0) {
    const char = base64chars.charAt(random % 64);
    result += char;
    random = Math.floor(random / 64);
  }
  return result;
}
// ==

export default class AppInsightsService {
  private static isInitialized = false;

  public static downloadAndSetup(): void {
    if (!AppInsightsService.isInitialized && DEFAULT_CONFIG.instrumentationKey && typeof AppInsights.downloadAndSetup === 'function') {
      AppInsightsService.isInitialized = true;

      AppInsights.downloadAndSetup(DEFAULT_CONFIG);

      // AppInsights.queue.push(() => {
      //   AppInsights.context.addTelemetryInitializer((envelope) => {
      //     console.log(envelope);
      //     return false;
      //   });
      // });
    }
  }

  public static trackPageView(telemetry: PageViewTelemetry): void {
    AppInsightsService.invoke(
      'trackPageView',
      telemetry.name,
      telemetry.url,
      telemetry.properties,
      telemetry.measurements,
      telemetry.duration
    );
  }

  public static trackEvent(telemetry: EventTelemetry): void {
    AppInsightsService.invoke('trackEvent', telemetry.name, telemetry.properties, telemetry.measurements);
  }

  public static trackException(telemetry: ExceptionTelemetry): void {
    AppInsightsService.invoke(
      'trackException',
      telemetry.exception,
      undefined,
      telemetry.properties,
      telemetry.measurements,
      telemetry.severityLevel
    );
  }

  public static trackTrace(telemetry: TraceTelemetry): void {
    AppInsightsService.invoke('trackTrace', telemetry.message, telemetry.properties, telemetry.severityLevel);
  }

  public static trackAjaxRequest(telemetry: HttpRequestTelemetry): void {
    AppInsightsService.invoke(
      'trackDependency',
      newId(),
      telemetry.method,
      telemetry.url,
      telemetry.name,
      telemetry.duration,
      telemetry.success,
      telemetry.resultCode
    );
  }

  // tslint:disable-next-line:no-any
  private static invoke(methodName: IAppInsightsMethods, ...args: any[]): any {
    if (!AppInsightsService.isInitialized) {
      return;
    }

    try {
      // @ts-ignore Expected 8-10 arguments, but got 1 or more
      return AppInsights[methodName].call(AppInsights, ...args);
    } catch (error) {
      if (process.env.REACT_APP_ENV !== 'production') {
        console.log(`ERROR: AppInsightsService::invoke: <${methodName}> ${error}`);
      }
    }
  }
}
